import axios from 'axios';

import { getRefreshToken, setTokenStorage } from './tokenStorage';
import type { IOAuth2Config, IToken } from './types';

/**
 * The parts of the OAuth2 config a refresh needs: the token endpoint (the same one the
 * authorization code is exchanged at) and the client id. Narrowed from IOAuth2Config so the
 * signature says what this actually depends on, while still accepting the whole config.
 */
export type IRefreshConfig = Pick<IOAuth2Config, 'tokenUrl' | 'clientId'>;

let refreshConfig: IRefreshConfig | undefined;

/**
 * Registers the token endpoint and client id used to refresh the access token.
 * AuthProvider does this from its oauth2Config prop; call it directly only outside that provider.
 */
export const setRefreshConfig = (config: IRefreshConfig): void => {
  refreshConfig = config;
};

export const getRefreshUrl = (): string | undefined => refreshConfig?.tokenUrl;

export const getRefreshClientId = (): string | undefined => refreshConfig?.clientId;

/**
 * A bare client, deliberately not the shared instance.
 *
 * The refresh call must carry neither the access token nor the org header. The gateway exempts
 * only the exact path /oauth2/token from token verification, and the app's token URL is proxied
 * under /ims, so an attached access token gets verified there - which fails with a 401 in exactly
 * the situation a refresh is needed. Using the shared instance would also put its own 401
 * interceptor in the refresh path and loop.
 */
const refreshClient = axios.create();

let inFlight: Promise<IToken> | null = null;

type TokenRefreshListener = (token: IToken) => void;

const listeners = new Set<TokenRefreshListener>();

/**
 * Subscribes to token refreshes.
 *
 * A refresh happens inside an interceptor, outside React, so anything holding the token in state
 * would otherwise keep serving the expired one for the rest of the session.
 *
 * @param listener called with the refreshed token
 * @returns an unsubscribe function
 */
export const onTokenRefreshed = (
  listener: TokenRefreshListener
): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

/**
 * Exchanges the stored refresh token for a new access token and persists both.
 *
 * Concurrent callers share one request: several requests failing with 401 at the same time must
 * not each spend the refresh token, because the server rotates it and only the first call would
 * succeed.
 *
 * @returns the refreshed token
 * @throws if refresh is not configured, no refresh token is stored, or the server rejects it
 */
export const refreshAccessToken = (): Promise<IToken> => {
  if (inFlight) {
    return inFlight;
  }

  inFlight = performRefresh().finally(() => {
    inFlight = null;
  });

  return inFlight;
};

const performRefresh = async (): Promise<IToken> => {
  if (!refreshConfig) {
    throw new Error(
      'Refresh is not configured. Pass oauth2Config to AuthProvider.'
    );
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token stored');
  }

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: refreshConfig.clientId,
  });

  const { data } = await refreshClient.post(refreshConfig.tokenUrl, body, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });

  const accessToken: string | undefined = data?.access_token;
  if (!accessToken) {
    throw new Error('No access_token in refresh response');
  }

  // The server rotates refresh tokens, so the one just used is already dead - store the new one
  // or the next refresh fails.
  const token: IToken = {
    accessToken,
    refreshToken: data?.refresh_token ?? refreshToken,
  };

  setTokenStorage(token);
  listeners.forEach(listener => listener(token));

  return token;
};
